/**
 * BookingContext - Contexto global para el estado del agendamiento
 * Mantiene todas las selecciones del usuario durante el wizard
 */

import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { TherapistExtended } from '../../types';
import type { MetodoPagoDeclarado } from '../../lib/utils/pricing';

// Tipos para el nuevo flujo optimizado
export interface TherapyType {
  id: 'individual' | 'pareja' | 'familia';
  name: string;
  description: string;
  icon: string;
  durations: {
    id: string;
    name: string;
    minutes: number;
    price: number;
  }[];
}

export interface SelectedService {
  id: string;
  therapyType: 'individual' | 'pareja' | 'familia';
  name: string;
  duration: number; // en minutos
  price: number;
  modality: 'presencial' | 'online' | 'ambas' | 'domicilio';
}

export interface SelectedTimeSlot {
  id: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
}

// Método de pago declarado por el paciente al agendar (Fase 0.5).
// No es un pago real todavía — solo la intención declarada, ver
// sql/migrations/metodo_pago_declarado_booking_2026_07_15.sql.
// FUNC-131: 'tarjeta' = débito/crédito (precio de lista). Definido en lib/utils/pricing.ts.
export type { MetodoPagoDeclarado };

// SEC-057 (2026-09-21): `search_patient_by_rut` dejó de devolver `email`/`telefono`
// — filtraban contacto completo de CUALQUIER paciente vía RUT adivinado, sin login
// (ver sql/migrations/sec057_pii_idor_rut_scope_2026_09_21.sql). El email/teléfono
// real del paciente existente ahora se recolecta en un paso propio dentro de
// ExistingPatientBooking.tsx (gate de contacto), nunca se lee de este objeto.
export interface ExistingPatient {
  id: string;
  nombre: string;
  apellido: string;
  rut: string;
  therapistId?: string;
  tarifa_diferenciada?: number;
}

export interface PatientData {
  nombre: string;
  apellido: string;
  email: string;
  telefono: string;
  rut: string;
  genero: string;
  nombrePareja: string;
  direccion: string;
  motivoConsulta: string;
  antecedentesMedicos: string;
  medicamentos: string;
  emergenciaContacto: string;
  emergenciaTelefono: string;
  preferenciaComunicacion: string;
  // FUNC-114 (Ley 21.719): consentimiento de datos clínicos/sensibles y de
  // contacto-marketing van separados — son finalidades distintas, no se
  // pueden bundlear en un solo checkbox.
  consentimientoDatosClinicos: boolean;
  consentimientoContactoMarketing: boolean;
  terminosCondiciones: boolean;
}

export interface BookingState {
  // Verificación de paciente
  patientVerification: {
    rut: string;
    isExisting: boolean;
    existingPatient: ExistingPatient | null;
    isExtranjero?: boolean;
  } | null;
  
  // Selecciones del usuario
  selectedTherapyType: TherapyType | null;
  selectedTherapist: TherapistExtended | null;
  selectedService: SelectedService | null;
  selectedTimeSlot: SelectedTimeSlot | null;
  patientData: PatientData | null;
  metodoPagoDeclarado: MetodoPagoDeclarado;

  // Roadmap Feature 1 — nudge de nombre_pareja (docs/plan-ruta-terapeutica-pareja.md §6.3).
  // `null` = todavía no se consultó / está cargando; lo comparten la tarjeta inline de
  // ExistingPatientBooking y Konex (konexBrain) para no duplicar la llamada a la RPC.
  parejaNudge: boolean | null;

  // Estados del sistema
  isLoading: boolean;
  error: string | null;
}

// Acciones del reducer para el nuevo flujo
export type BookingAction =
  | { type: 'SET_PATIENT_VERIFICATION'; payload: { rut: string; isExisting: boolean; existingPatient: ExistingPatient | null; isExtranjero?: boolean } }
  | { type: 'SET_THERAPY_TYPE'; payload: TherapyType }
  | { type: 'SET_THERAPIST'; payload: TherapistExtended }
  | { type: 'SET_SERVICE'; payload: SelectedService }
  | { type: 'SET_TIMESLOT'; payload: SelectedTimeSlot }
  | { type: 'SET_PATIENT_DATA'; payload: PatientData }
  | { type: 'SET_METODO_PAGO_DECLARADO'; payload: MetodoPagoDeclarado }
  | { type: 'SET_PAREJA_NUDGE'; payload: boolean | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_TIMESLOT' }
  | { type: 'CLEAR_BOOKING' }
  | { type: 'RESET_BOOKING_KEEP_PATIENT' };

// Estado inicial
const initialState: BookingState = {
  patientVerification: null,
  selectedTherapyType: null,
  selectedTherapist: null,
  selectedService: null,
  selectedTimeSlot: null,
  patientData: null,
  metodoPagoDeclarado: 'transferencia',
  parejaNudge: null,
  isLoading: false,
  error: null
};

// Reducer actualizado
const bookingReducer = (state: BookingState, action: BookingAction): BookingState => {
  switch (action.type) {
    case 'SET_PATIENT_VERIFICATION':
      return { ...state, patientVerification: action.payload, error: null };
    case 'SET_THERAPY_TYPE':
      return { ...state, selectedTherapyType: action.payload, error: null };
    case 'SET_THERAPIST':
      return { ...state, selectedTherapist: action.payload, error: null };
    case 'SET_SERVICE':
      return { ...state, selectedService: action.payload, error: null };
    case 'SET_TIMESLOT':
      return { ...state, selectedTimeSlot: action.payload, error: null };
    case 'CLEAR_TIMESLOT':
      return { ...state, selectedTimeSlot: null };
    case 'SET_PATIENT_DATA':
      return { ...state, patientData: action.payload, error: null };
    case 'SET_METODO_PAGO_DECLARADO':
      return { ...state, metodoPagoDeclarado: action.payload };
    case 'SET_PAREJA_NUDGE':
      return { ...state, parejaNudge: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR_BOOKING':
      return { ...initialState };
    case 'RESET_BOOKING_KEEP_PATIENT':
      return { ...initialState, patientVerification: state.patientVerification };
    default:
      return state;
  }
};

// Contexto actualizado
interface BookingContextType {
  state: BookingState;
  dispatch: React.Dispatch<BookingAction>;
  
  // Nuevas funciones
  setPatientVerification: (verification: { rut: string; isExisting: boolean; existingPatient: ExistingPatient | null; isExtranjero?: boolean }) => void;
  setTherapyType: (therapyType: TherapyType) => void;
  
  // Funciones existentes
  setTherapist: (therapist: TherapistExtended) => void;
  setService: (service: SelectedService) => void;
  setTimeSlot: (timeSlot: SelectedTimeSlot) => void;
  setPatientData: (patientData: PatientData) => void;
  setMetodoPagoDeclarado: (metodo: MetodoPagoDeclarado) => void;
  setParejaNudge: (value: boolean | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearBooking: () => void;
  
  // Utilidades
  isStepComplete: (step: string) => boolean;
  getBookingSummary: () => string;
  isExistingPatient: () => boolean;
}

const BookingContext = createContext<BookingContextType | undefined>(undefined);

// Provider actualizado
export const BookingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(bookingReducer, initialState);

  // Dispatch wrappers — useCallback con [] porque dispatch de useReducer es estable
  const setPatientVerification = useCallback((verification: { rut: string; isExisting: boolean; existingPatient: ExistingPatient | null; isExtranjero?: boolean }) => {
    dispatch({ type: 'SET_PATIENT_VERIFICATION', payload: verification });
  }, []);

  const setTherapyType = useCallback((therapyType: TherapyType) => {
    dispatch({ type: 'SET_THERAPY_TYPE', payload: therapyType });
  }, []);

  const setTherapist = useCallback((therapist: TherapistExtended) => {
    dispatch({ type: 'SET_THERAPIST', payload: therapist });
  }, []);

  const setService = useCallback((service: SelectedService) => {
    dispatch({ type: 'SET_SERVICE', payload: service });
  }, []);

  const setTimeSlot = useCallback((timeSlot: SelectedTimeSlot) => {
    dispatch({ type: 'SET_TIMESLOT', payload: timeSlot });
  }, []);

  const setPatientData = useCallback((patientData: PatientData) => {
    dispatch({ type: 'SET_PATIENT_DATA', payload: patientData });
  }, []);

  const setMetodoPagoDeclarado = useCallback((metodo: MetodoPagoDeclarado) => {
    dispatch({ type: 'SET_METODO_PAGO_DECLARADO', payload: metodo });
  }, []);

  const setParejaNudge = useCallback((value: boolean | null) => {
    dispatch({ type: 'SET_PAREJA_NUDGE', payload: value });
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  }, []);

  const clearBooking = useCallback(() => {
    dispatch({ type: 'CLEAR_BOOKING' });
  }, []);

  const isStepComplete = (step: string): boolean => {
    switch (step) {
      case 'service-therapist':
        return state.selectedService !== null && state.selectedTherapist !== null;
      case 'calendar':
        return state.selectedTimeSlot !== null;
      case 'patient-confirmation':
        return state.patientData !== null || state.patientVerification?.isExisting === true;
      // Mantenemos compatibilidad con nombres anteriores
      case 'service':
        return state.selectedService !== null && state.patientVerification !== null;
      case 'therapist':
        return state.selectedTherapist !== null;
      case 'patient':
        return state.patientData !== null || state.patientVerification?.isExisting === true;
      case 'confirmation':
        return false; // Nunca está "completo" hasta que se confirme
      default:
        return false;
    }
  };

  const isExistingPatient = (): boolean => {
    return state.patientVerification?.isExisting === true;
  };

  const getBookingSummary = (): string => {
    const { selectedTherapist, selectedService, selectedTimeSlot, selectedTherapyType } = state;
    if (!selectedTherapist || !selectedService || !selectedTimeSlot || !selectedTherapyType) {
      return 'Información incompleta';
    }
    
    return `${selectedTherapyType.name} con ${selectedTherapist.name} - ${selectedTimeSlot.date} ${selectedTimeSlot.time}`;
  };

  const contextValue: BookingContextType = {
    state,
    dispatch,
    
    // Nuevas funciones
    setPatientVerification,
    setTherapyType,
    
    // Funciones existentes
    setTherapist,
    setService,
    setTimeSlot,
    setPatientData,
    setMetodoPagoDeclarado,
    setParejaNudge,
    setLoading,
    setError,
    clearBooking,
    
    // Utilidades
    isStepComplete,
    getBookingSummary,
    isExistingPatient
  };

  return (
    <BookingContext.Provider value={contextValue}>
      {children}
    </BookingContext.Provider>
  );
};

// Hook personalizado
export const useBooking = (): BookingContextType => {
  const context = useContext(BookingContext);
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};